/**
 * Track SFM — Smart forms: the rules of a form, in one file with no imports.
 *
 * 🔴 BYTE-IDENTICAL in two codebases (SFM-D5):
 *   - RxManager_BE  src/modules/forms/logic/form-logic.ts
 *   - RxManager_FE  packages/forms/src/logic/form-logic.ts
 * Each copy is pinned by a sha256 test, and both run the same vectors
 * (form-logic.vectors.json, also byte-identical). Change one, copy it to the
 * other, then update both pins. RxManager_BE's prettier formats it (80
 * columns); RxManager_FE's .prettierignore leaves it alone.
 *
 * What lives here is what the server, the patient's page and the builder's
 * preview must agree on exactly: which questions are visible, which are
 * required, whether an answer is acceptable, the score, which languages a form
 * can be offered in, what stops a form being published, and how a submission
 * reads back. Pure functions over plain data: no clock (callers pass `today`),
 * no randomness, no IO.
 *
 * Anything that arrives as JSON (a draft from the builder, answers from a
 * phone) is `unknown` here until a function below has checked it.
 */

/** Bumped when a rule here changes meaning; stored on every published version. */
export const FORM_LOGIC_VERSION = 'sfm-2';

export type Lang = 'en' | 'fr';

/** Words in English, optionally French. */
export type Text = { en: string; fr?: string };

export const QUESTION_TYPES = [
  'short_text',
  'long_text',
  'single_choice',
  'multi_choice',
  'yes_no',
  'date',
  'number',
  'info',
  'consent',
  'signature',
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

export type Option = { id: string; label: Text; points?: number };

export type ShowIfOp = 'is' | 'is_not' | 'any_of' | 'answered';

/** "Show this question only if <an earlier question> <op> <value(s)>" (SFM-D2). */
export type ShowIf = {
  question: string;
  op: ShowIfOp;
  value?: string;
  values?: string[];
};

/**
 * "Stop the booking" (SFM-D27): answers that mean the patient cannot book this
 * online. Booking online, the patient reads `message` and cannot go on until
 * they change the answer; on a form sent by link, the answer is accepted and
 * the message shown after sending, so the pharmacist still sees it.
 */
export type StopRule = {
  /** yes/no: `'yes'` | `'no'`; choices: option ids. Never every answer. */
  answers: string[];
  message: Text;
};

type QuestionBase = {
  id: string;
  label: Text;
  help?: Text;
  required?: boolean;
  showIf?: ShowIf;
};

export type ShortTextQuestion = QuestionBase & {
  type: 'short_text';
  maxLength?: number;
};
export type LongTextQuestion = QuestionBase & {
  type: 'long_text';
  maxLength?: number;
};
export type SingleChoiceQuestion = QuestionBase & {
  type: 'single_choice';
  options: Option[];
  stop?: StopRule;
};
export type MultiChoiceQuestion = QuestionBase & {
  type: 'multi_choice';
  options: Option[];
  maxSelected?: number;
  stop?: StopRule;
};
export type YesNoQuestion = QuestionBase & {
  type: 'yes_no';
  points?: { yes?: number; no?: number };
  stop?: StopRule;
};
export type DateQuestion = QuestionBase & {
  type: 'date';
  range?: 'past' | 'future' | 'any';
};
export type NumberQuestion = QuestionBase & {
  type: 'number';
  min?: number;
  max?: number;
  integer?: boolean;
  unit?: Text;
};
/** Words shown on their own screen. The label is the text; nothing is asked. */
export type InfoQuestion = QuestionBase & { type: 'info' };
/** A statement the patient agrees to. The label is a short title. */
export type ConsentQuestion = QuestionBase & {
  type: 'consent';
  statement: Text;
};
export type SignatureQuestion = QuestionBase & { type: 'signature' };

export type Question =
  | ShortTextQuestion
  | LongTextQuestion
  | SingleChoiceQuestion
  | MultiChoiceQuestion
  | YesNoQuestion
  | DateQuestion
  | NumberQuestion
  | InfoQuestion
  | ConsentQuestion
  | SignatureQuestion;

/** A score range staff see (SFM-D3). `max` absent = no upper end. */
export type Band = {
  key: string;
  min: number;
  max?: number;
  label: Text;
  /** Marks a submission for attention (shown as "Review" to staff). */
  flag?: boolean;
};

export type FormSettings = {
  /** Only someone with Pharmacist-only actions may mark it reviewed (SFM-D13). */
  pharmacistReview?: boolean;
};

export type FormSchema = {
  schemaVersion: 1;
  title: Text;
  intro?: Text;
  questions: Question[];
  scoring?: { bands: Band[] };
  settings?: FormSettings;
};

export type SignatureAnswer =
  | { kind: 'drawn'; png: string }
  | { kind: 'typed'; name: string };

/**
 * One answer, by question type:
 * - short and long text: the string
 * - single choice: the option id; multiple choice: option ids
 * - yes/no: `'yes'` | `'no'`
 * - date: `YYYY-MM-DD`
 * - number: a finite number
 * - consent: `true`
 * - signature: a drawn PNG (base64, no `data:` prefix) or a typed name
 */
export type Answer = string | string[] | number | true | SignatureAnswer;
export type Answers = Record<string, Answer>;

export const FORM_LIMITS = {
  questions: 60,
  optionsMin: 2,
  optionsMax: 20,
  title: 120,
  intro: 1000,
  label: 300,
  help: 500,
  infoText: 2000,
  statement: 2000,
  optionLabel: 120,
  unit: 24,
  shortText: 200,
  longText: 2000,
  numberAbs: 1_000_000_000,
  typedNameMin: 2,
  typedNameMax: 120,
  /** About 100 KB of PNG; the server also checks the decoded image. */
  signatureBase64: 140_000,
  signatures: 3,
  consents: 10,
  bands: 6,
  bandLabel: 120,
  points: 99,
  stopMessage: 500,
  /** Characters of JSON, signatures not counted. */
  answersJson: 64 * 1024,
  schemaJson: 200 * 1024,
} as const;

const QUESTION_ID = /^q_[a-z0-9]{4,24}$/;
const OPTION_ID = /^o_[a-z0-9]{4,24}$/;
const BAND_KEY = /^[a-z][a-z0-9_]{0,47}$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;

// ─── Reading untrusted JSON ─────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is Text {
  return (
    isRecord(value) &&
    typeof value.en === 'string' &&
    (value.fr === undefined || typeof value.fr === 'string')
  );
}

function isQuestionType(value: unknown): value is QuestionType {
  return (
    typeof value === 'string' &&
    (QUESTION_TYPES as readonly string[]).includes(value)
  );
}

function hasText(text: Text | undefined, lang: Lang): boolean {
  if (!text) return false;
  const words = lang === 'en' ? text.en : text.fr;
  return typeof words === 'string' && words.trim().length > 0;
}

/** The words in `lang`, falling back to English. */
export function textIn(text: Text | undefined, lang: Lang): string {
  if (!text) return '';
  if (lang === 'fr' && typeof text.fr === 'string' && text.fr.trim()) {
    return text.fr;
  }
  return text.en;
}

function isChoice(
  q: Question,
): q is SingleChoiceQuestion | MultiChoiceQuestion {
  return q.type === 'single_choice' || q.type === 'multi_choice';
}

/** The questions a patient answers (everything but info screens). */
export function isAnswerable(q: Question): boolean {
  return q.type !== 'info';
}

// ─── Visibility ─────────────────────────────────────────────────────────────

/** Whether `value` counts as an answer to `q` (an empty string does not). */
export function hasAnswer(q: Question, value: unknown): boolean {
  if (value === undefined || value === null) return false;
  switch (q.type) {
    case 'short_text':
    case 'long_text':
    case 'date':
    case 'single_choice':
      return typeof value === 'string' && value.trim().length > 0;
    case 'multi_choice':
      return Array.isArray(value) && value.length > 0;
    case 'yes_no':
      return value === 'yes' || value === 'no';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'consent':
      return value === true;
    case 'signature':
      return (
        isRecord(value) &&
        ((value.kind === 'drawn' &&
          typeof value.png === 'string' &&
          value.png.length > 0) ||
          (value.kind === 'typed' &&
            typeof value.name === 'string' &&
            value.name.trim().length > 0))
      );
    case 'info':
      return false;
  }
}

function conditionHolds(
  target: Question,
  condition: ShowIf,
  value: unknown,
): boolean {
  const answered = hasAnswer(target, value);
  if (condition.op === 'answered') return answered;
  if (!answered) return false;
  const wanted =
    condition.op === 'any_of' ? (condition.values ?? []) : [condition.value];
  if (target.type === 'multi_choice') {
    const picked = value as string[];
    if (condition.op === 'is') return picked.includes(condition.value ?? '');
    if (condition.op === 'is_not') {
      return !picked.includes(condition.value ?? '');
    }
    return picked.some((id) => wanted.includes(id));
  }
  if (target.type === 'single_choice' || target.type === 'yes_no') {
    const picked = value as string;
    if (condition.op === 'is') return picked === condition.value;
    if (condition.op === 'is_not') return picked !== condition.value;
    return wanted.includes(picked);
  }
  // Any other type only supports `answered` (checked by schemaProblems).
  return false;
}

/**
 * The questions the patient sees, in order, for these answers.
 *
 * A condition may only look back (schemaProblems refuses a forward reference),
 * so one pass decides. A question whose condition names a hidden question
 * counts that question as unanswered; one that names a question that is not
 * there at all stays hidden.
 */
export function visibleQuestionIds(
  schema: FormSchema,
  answers: Answers,
): string[] {
  const seen = new Map<string, Question>();
  const visible = new Set<string>();
  const out: string[] = [];
  for (const q of schema.questions) {
    let show = true;
    if (q.showIf) {
      const target = seen.get(q.showIf.question);
      if (!target) {
        show = false;
      } else {
        const value = visible.has(target.id) ? answers[target.id] : undefined;
        show = conditionHolds(target, q.showIf, value);
      }
    }
    seen.set(q.id, q);
    if (show) {
      visible.add(q.id);
      out.push(q.id);
    }
  }
  return out;
}

/** Required unless it says not, for consents and signatures; never for info. */
export function isRequired(q: Question): boolean {
  if (q.type === 'info') return false;
  return q.required ?? (q.type === 'consent' || q.type === 'signature');
}

/** Where the patient is: `position` of `total` visible questions (1-based). */
export function progressOf(
  schema: FormSchema,
  answers: Answers,
  currentId: string,
): { position: number; total: number } {
  const ids = visibleQuestionIds(schema, answers);
  const index = ids.indexOf(currentId);
  return { position: index < 0 ? 0 : index + 1, total: ids.length };
}

// ─── Answers ────────────────────────────────────────────────────────────────

export type AnswerError =
  | 'required'
  | 'invalid'
  | 'too_long'
  | 'not_an_option'
  | 'too_many'
  | 'not_a_date'
  | 'date_in_future'
  | 'date_in_past'
  | 'not_a_number'
  | 'below_min'
  | 'above_max'
  | 'not_integer'
  | 'must_agree'
  | 'signature_missing'
  | 'name_required';

/** `YYYY-MM-DD` that is a real calendar day. */
export function isIsoDate(value: string): boolean {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function textLimit(q: ShortTextQuestion | LongTextQuestion): number {
  const cap =
    q.type === 'short_text' ? FORM_LIMITS.shortText : FORM_LIMITS.longText;
  return typeof q.maxLength === 'number' && q.maxLength > 0
    ? Math.min(q.maxLength, cap)
    : cap;
}

/**
 * Why `value` is not an acceptable answer to `q`, or null when it is.
 * A missing answer is not judged here; see `validateSubmission`.
 * `today` is `YYYY-MM-DD` in the pharmacy's time zone.
 */
export function validateAnswer(
  q: Question,
  value: unknown,
  ctx: { today: string },
): AnswerError | null {
  switch (q.type) {
    case 'short_text':
    case 'long_text': {
      if (typeof value !== 'string') return 'invalid';
      return value.trim().length > textLimit(q) ? 'too_long' : null;
    }
    case 'single_choice': {
      if (typeof value !== 'string') return 'invalid';
      return q.options.some((o) => o.id === value) ? null : 'not_an_option';
    }
    case 'multi_choice': {
      if (!Array.isArray(value)) return 'invalid';
      if (value.some((id) => typeof id !== 'string')) return 'invalid';
      if (new Set(value).size !== value.length) return 'invalid';
      if (!value.every((id) => q.options.some((o) => o.id === id))) {
        return 'not_an_option';
      }
      if (typeof q.maxSelected === 'number' && value.length > q.maxSelected) {
        return 'too_many';
      }
      return null;
    }
    case 'yes_no':
      return value === 'yes' || value === 'no' ? null : 'invalid';
    case 'date': {
      if (typeof value !== 'string' || !isIsoDate(value)) return 'not_a_date';
      if (q.range === 'past' && value > ctx.today) return 'date_in_future';
      if (q.range === 'future' && value < ctx.today) return 'date_in_past';
      return null;
    }
    case 'number': {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return 'not_a_number';
      }
      if (Math.abs(value) > FORM_LIMITS.numberAbs) return 'above_max';
      if (q.integer && !Number.isInteger(value)) return 'not_integer';
      if (typeof q.min === 'number' && value < q.min) return 'below_min';
      if (typeof q.max === 'number' && value > q.max) return 'above_max';
      return null;
    }
    case 'consent':
      return value === true ? null : 'must_agree';
    case 'signature': {
      if (!isRecord(value)) return 'signature_missing';
      if (value.kind === 'drawn') {
        const png = value.png;
        if (typeof png !== 'string' || png.length === 0) {
          return 'signature_missing';
        }
        if (png.length > FORM_LIMITS.signatureBase64 || !BASE64.test(png)) {
          return 'invalid';
        }
        return null;
      }
      if (value.kind === 'typed') {
        const name = typeof value.name === 'string' ? value.name.trim() : '';
        if (
          name.length < FORM_LIMITS.typedNameMin ||
          name.length > FORM_LIMITS.typedNameMax
        ) {
          return 'name_required';
        }
        return null;
      }
      return 'signature_missing';
    }
    case 'info':
      return 'invalid';
  }
}

function missingError(q: Question): AnswerError {
  if (q.type === 'consent') return 'must_agree';
  if (q.type === 'signature') return 'signature_missing';
  return 'required';
}

/** JSON length of answers with signatures left out (they have their own cap). */
function answersSize(answers: Record<string, unknown>): number {
  let size = 2;
  for (const [id, value] of Object.entries(answers)) {
    const counted =
      isRecord(value) && value.kind === 'drawn' ? '' : JSON.stringify(value);
    size += id.length + (counted?.length ?? 0) + 4;
  }
  return size;
}

/**
 * A draft saved part-way: keeps answers to questions that exist and are of the
 * right kind, drops the rest. Ranges and required are not judged until submit,
 * so a half-typed answer survives a dropped connection.
 */
export function validateDraft(
  schema: FormSchema,
  input: unknown,
): { ok: true; answers: Answers } | { ok: false; error: 'too_large' } {
  const answers: Answers = {};
  if (!isRecord(input)) return { ok: true, answers };
  if (answersSize(input) > FORM_LIMITS.answersJson) {
    return { ok: false, error: 'too_large' };
  }
  for (const q of schema.questions) {
    if (!isAnswerable(q)) continue;
    const value = input[q.id];
    if (!hasAnswer(q, value)) continue;
    const error = validateAnswer(q, value, { today: '9999-12-31' });
    if (error === 'invalid' || error === 'not_a_date') continue;
    if (error === 'not_a_number' || error === 'signature_missing') continue;
    answers[q.id] = value as Answer;
  }
  return { ok: true, answers };
}

export type SubmissionCheck =
  | { ok: true; answers: Answers; visible: string[] }
  | { ok: false; errors: Record<string, AnswerError> };

/**
 * The final check before a form is accepted. Every visible question is judged;
 * answers to hidden or unknown questions are dropped (SFM-D2), so a patient
 * who changed an earlier answer leaves nothing stale behind. `visible` is kept
 * with the submission so staff can see what was skipped.
 */
export function validateSubmission(
  schema: FormSchema,
  input: unknown,
  ctx: { today: string },
): SubmissionCheck {
  const raw: Record<string, unknown> = isRecord(input) ? input : {};
  const kept: Answers = {};
  for (const q of schema.questions) {
    const value = raw[q.id];
    if (isAnswerable(q) && hasAnswer(q, value)) kept[q.id] = value as Answer;
  }
  const visible = visibleQuestionIds(schema, kept);
  const errors: Record<string, AnswerError> = {};
  const answers: Answers = {};
  const byId = new Map(schema.questions.map((q) => [q.id, q]));
  for (const id of visible) {
    const q = byId.get(id);
    if (!q || !isAnswerable(q)) continue;
    const value = kept[id];
    if (value === undefined) {
      if (isRequired(q)) errors[id] = missingError(q);
      continue;
    }
    const error = validateAnswer(q, value, ctx);
    if (error) {
      errors[id] = error;
      continue;
    }
    answers[id] =
      typeof value === 'string' && q.type !== 'single_choice'
        ? value.trim()
        : value;
  }
  if (answersSize(answers) > FORM_LIMITS.answersJson) {
    errors._form = 'too_long';
  }
  return Object.keys(errors).length > 0
    ? { ok: false, errors }
    : { ok: true, answers, visible };
}

// ─── Scoring ────────────────────────────────────────────────────────────────

export type ScoreResult = {
  total: number;
  band: Band | null;
  flagged: boolean;
};

function pointsFor(q: Question, value: Answer): number {
  if (q.type === 'single_choice' && typeof value === 'string') {
    return q.options.find((o) => o.id === value)?.points ?? 0;
  }
  if (q.type === 'multi_choice' && Array.isArray(value)) {
    return value.reduce(
      (sum, id) => sum + (q.options.find((o) => o.id === id)?.points ?? 0),
      0,
    );
  }
  if (q.type === 'yes_no' && (value === 'yes' || value === 'no')) {
    return q.points?.[value] ?? 0;
  }
  return 0;
}

/**
 * The total of points over visible, answered choice and yes/no questions, and
 * the first band it falls in. Null when the form has no scoring. Staff-only:
 * `patientView` strips points and bands.
 */
export function score(
  schema: FormSchema,
  answers: Answers,
): ScoreResult | null {
  if (!schema.scoring) return null;
  const visible = new Set(visibleQuestionIds(schema, answers));
  let total = 0;
  for (const q of schema.questions) {
    const value = answers[q.id];
    if (!visible.has(q.id) || value === undefined) continue;
    total += pointsFor(q, value);
  }
  const band =
    schema.scoring.bands.find(
      (b) => total >= b.min && (b.max === undefined || total <= b.max),
    ) ?? null;
  return { total, band, flagged: band?.flag === true };
}

// ─── Stopping ───────────────────────────────────────────────────────────────

export type StopHit = { questionId: string; message: Text };

/** The stop rule of a yes/no or choice question; other types have none. */
function stopRuleOf(q: Question): StopRule | undefined {
  return q.type === 'yes_no' || isChoice(q) ? q.stop : undefined;
}

/** Whether `value`, as an answer to `q`, is one its stop rule names. */
export function stopsOn(q: Question, value: unknown): boolean {
  const rule = stopRuleOf(q);
  if (!rule || !hasAnswer(q, value)) return false;
  const picked = Array.isArray(value) ? value : [value];
  return picked.some((v) => typeof v === 'string' && rule.answers.includes(v));
}

/**
 * The first visible question, in form order, whose answer stops the booking
 * (SFM-D27); null when none does. Hidden and unanswered questions never stop.
 */
export function stoppedBy(
  schema: FormSchema,
  answers: Answers,
): StopHit | null {
  const visible = new Set(visibleQuestionIds(schema, answers));
  for (const q of schema.questions) {
    const rule = stopRuleOf(q);
    if (rule && visible.has(q.id) && stopsOn(q, answers[q.id])) {
      return { questionId: q.id, message: rule.message };
    }
  }
  return null;
}

// ─── What the patient is sent ───────────────────────────────────────────────

/** The form without points, bands or staff settings. */
export function patientView(schema: FormSchema): FormSchema {
  const questions = schema.questions.map((q): Question => {
    if (isChoice(q)) {
      return {
        ...q,
        options: q.options.map((o) => ({ id: o.id, label: o.label })),
      };
    }
    if (q.type === 'yes_no') {
      const copy: YesNoQuestion = { ...q };
      delete copy.points;
      return copy;
    }
    return { ...q };
  });
  const view: FormSchema = {
    schemaVersion: 1,
    title: schema.title,
    questions,
  };
  if (schema.intro) view.intro = schema.intro;
  return view;
}

/** Every patient-facing text in the form. */
function patientTexts(schema: FormSchema): Text[] {
  const texts: Text[] = [schema.title];
  if (schema.intro) texts.push(schema.intro);
  for (const q of schema.questions) {
    texts.push(q.label);
    if (q.help) texts.push(q.help);
    if (isChoice(q)) for (const o of q.options) texts.push(o.label);
    const rule = stopRuleOf(q);
    if (rule) texts.push(rule.message);
    if (q.type === 'consent') texts.push(q.statement);
    if (q.type === 'number' && q.unit) texts.push(q.unit);
  }
  return texts;
}

/** `['en']`, or `['en', 'fr']` once every patient-facing text has French (SFM-D4). */
export function languagesOf(schema: FormSchema): Lang[] {
  const texts = patientTexts(schema);
  return texts.length > 0 && texts.every((t) => hasText(t, 'fr'))
    ? ['en', 'fr']
    : ['en'];
}

/** How much of the form has French, for the builder's "French 7 of 9". */
export function frenchCoverage(schema: FormSchema): {
  done: number;
  total: number;
} {
  const texts = patientTexts(schema);
  return {
    done: texts.filter((t) => hasText(t, 'fr')).length,
    total: texts.length,
  };
}

// ─── Publishing ─────────────────────────────────────────────────────────────

export type ProblemCode =
  | 'not_a_form'
  | 'too_large'
  | 'title_missing'
  | 'title_too_long'
  | 'intro_too_long'
  | 'no_questions'
  | 'too_many_questions'
  | 'question_not_valid'
  | 'bad_question_id'
  | 'duplicate_question_id'
  | 'unknown_type'
  | 'label_missing'
  | 'label_too_long'
  | 'help_too_long'
  | 'text_not_valid'
  | 'too_few_options'
  | 'too_many_options'
  | 'bad_option_id'
  | 'duplicate_option'
  | 'option_label_missing'
  | 'option_label_too_long'
  | 'bad_points'
  | 'points_not_allowed'
  | 'bad_max_length'
  | 'bad_max_selected'
  | 'bad_number_range'
  | 'unit_too_long'
  | 'bad_date_range'
  | 'statement_missing'
  | 'statement_too_long'
  | 'too_many_signatures'
  | 'too_many_consents'
  | 'show_if_not_valid'
  | 'show_if_unknown_question'
  | 'show_if_forward'
  | 'show_if_bad_op'
  | 'show_if_bad_value'
  | 'show_if_on_info'
  | 'bad_required'
  | 'scoring_not_valid'
  | 'too_many_bands'
  | 'band_not_valid'
  | 'bad_band_key'
  | 'duplicate_band_key'
  | 'band_label_missing'
  | 'band_range_not_valid'
  | 'bands_overlap'
  | 'scoring_without_points'
  | 'french_incomplete'
  | 'stop_not_allowed'
  | 'stop_not_valid'
  | 'stop_bad_answer'
  | 'stop_every_answer'
  | 'stop_message_missing'
  | 'stop_message_too_long';

export type Problem = {
  code: ProblemCode;
  /** An error stops Publish; a warning does not. */
  severity: 'error' | 'warning';
  questionId?: string;
  optionId?: string;
  bandKey?: string;
};

function checkText(
  value: unknown,
  max: number,
  missing: ProblemCode | null,
  tooLong: ProblemCode,
  problems: Problem[],
  at: Partial<Problem>,
): void {
  if (value === undefined) {
    if (missing) problems.push({ code: missing, severity: 'error', ...at });
    return;
  }
  if (!isText(value)) {
    problems.push({ code: 'text_not_valid', severity: 'error', ...at });
    return;
  }
  if (missing && !hasText(value, 'en')) {
    problems.push({ code: missing, severity: 'error', ...at });
  }
  if (value.en.length > max || (value.fr ?? '').length > max) {
    problems.push({ code: tooLong, severity: 'error', ...at });
  }
}

function isPoints(value: unknown): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    Math.abs(value) <= FORM_LIMITS.points
  );
}

/** Which operators a condition on `target` may use. */
function opsFor(target: QuestionType): ShowIfOp[] {
  if (target === 'single_choice' || target === 'multi_choice') {
    return ['is', 'is_not', 'any_of', 'answered'];
  }
  if (target === 'yes_no') return ['is', 'is_not', 'answered'];
  if (target === 'info') return [];
  return ['answered'];
}

/** A question's stop rule (SFM-D27): on yes/no and choices, never every answer. */
function checkStop(
  rule: unknown,
  type: QuestionType,
  options: Set<string> | undefined,
  problems: Problem[],
  at: Partial<Problem>,
): void {
  if (
    type !== 'yes_no' &&
    type !== 'single_choice' &&
    type !== 'multi_choice'
  ) {
    problems.push({ code: 'stop_not_allowed', severity: 'error', ...at });
    return;
  }
  if (
    !isRecord(rule) ||
    !Array.isArray(rule.answers) ||
    !rule.answers.every((a) => typeof a === 'string')
  ) {
    problems.push({ code: 'stop_not_valid', severity: 'error', ...at });
    return;
  }
  const allowed = type === 'yes_no' ? ['yes', 'no'] : [...(options ?? [])];
  const picked = rule.answers.filter((a): a is string => typeof a === 'string');
  if (
    picked.length === 0 ||
    new Set(picked).size !== picked.length ||
    !picked.every((a) => allowed.includes(a))
  ) {
    problems.push({ code: 'stop_bad_answer', severity: 'error', ...at });
  } else if (allowed.every((a) => picked.includes(a))) {
    problems.push({ code: 'stop_every_answer', severity: 'error', ...at });
  }
  checkText(
    rule.message,
    FORM_LIMITS.stopMessage,
    'stop_message_missing',
    'stop_message_too_long',
    problems,
    at,
  );
}

/**
 * Everything that would stop this form being published (errors), or is worth
 * saying before it is (warnings). Takes `unknown`: it is what reads a draft.
 * A draft with problems can be saved; only Publish needs zero errors.
 */
export function schemaProblems(input: unknown): Problem[] {
  const problems: Problem[] = [];
  if (!isRecord(input) || input.schemaVersion !== 1) {
    return [{ code: 'not_a_form', severity: 'error' }];
  }
  if (JSON.stringify(input).length > FORM_LIMITS.schemaJson) {
    problems.push({ code: 'too_large', severity: 'error' });
  }
  checkText(
    input.title,
    FORM_LIMITS.title,
    'title_missing',
    'title_too_long',
    problems,
    {},
  );
  checkText(
    input.intro,
    FORM_LIMITS.intro,
    null,
    'intro_too_long',
    problems,
    {},
  );

  const list = input.questions;
  if (!Array.isArray(list)) {
    problems.push({ code: 'no_questions', severity: 'error' });
    return problems;
  }
  if (list.length > FORM_LIMITS.questions) {
    problems.push({ code: 'too_many_questions', severity: 'error' });
  }

  const earlier = new Map<string, QuestionType>();
  const optionIds = new Map<string, Set<string>>();
  let answerable = 0;
  let signatures = 0;
  let consents = 0;
  let pointsSeen = false;

  for (const item of list) {
    if (!isRecord(item)) {
      problems.push({ code: 'question_not_valid', severity: 'error' });
      continue;
    }
    const id = typeof item.id === 'string' ? item.id : '';
    const at = { questionId: id || undefined };
    if (!QUESTION_ID.test(id)) {
      problems.push({ code: 'bad_question_id', severity: 'error', ...at });
    } else if (earlier.has(id)) {
      problems.push({
        code: 'duplicate_question_id',
        severity: 'error',
        ...at,
      });
    }
    const type = item.type;
    if (!isQuestionType(type)) {
      problems.push({ code: 'unknown_type', severity: 'error', ...at });
      continue;
    }
    if (type !== 'info') answerable += 1;
    if (type === 'signature') signatures += 1;
    if (type === 'consent') consents += 1;

    checkText(
      item.label,
      type === 'info' ? FORM_LIMITS.infoText : FORM_LIMITS.label,
      'label_missing',
      'label_too_long',
      problems,
      at,
    );
    checkText(item.help, FORM_LIMITS.help, null, 'help_too_long', problems, at);
    if (item.required !== undefined && typeof item.required !== 'boolean') {
      problems.push({ code: 'bad_required', severity: 'error', ...at });
    }

    if (type === 'single_choice' || type === 'multi_choice') {
      const options = Array.isArray(item.options) ? item.options : [];
      if (options.length < FORM_LIMITS.optionsMin) {
        problems.push({ code: 'too_few_options', severity: 'error', ...at });
      }
      if (options.length > FORM_LIMITS.optionsMax) {
        problems.push({ code: 'too_many_options', severity: 'error', ...at });
      }
      const ids = new Set<string>();
      for (const option of options) {
        const optionId =
          isRecord(option) && typeof option.id === 'string' ? option.id : '';
        const atOption = { ...at, optionId: optionId || undefined };
        if (!OPTION_ID.test(optionId)) {
          problems.push({
            code: 'bad_option_id',
            severity: 'error',
            ...atOption,
          });
        } else if (ids.has(optionId)) {
          problems.push({
            code: 'duplicate_option',
            severity: 'error',
            ...atOption,
          });
        }
        ids.add(optionId);
        checkText(
          isRecord(option) ? option.label : undefined,
          FORM_LIMITS.optionLabel,
          'option_label_missing',
          'option_label_too_long',
          problems,
          atOption,
        );
        if (isRecord(option) && option.points !== undefined) {
          if (!isPoints(option.points)) {
            problems.push({
              code: 'bad_points',
              severity: 'error',
              ...atOption,
            });
          } else if (option.points !== 0) {
            pointsSeen = true;
          }
        }
      }
      if (id) optionIds.set(id, ids);
      if (type === 'multi_choice' && item.maxSelected !== undefined) {
        const max = item.maxSelected;
        if (
          typeof max !== 'number' ||
          !Number.isInteger(max) ||
          max < 1 ||
          max > options.length
        ) {
          problems.push({ code: 'bad_max_selected', severity: 'error', ...at });
        }
      }
    } else if (type === 'yes_no') {
      const points = item.points;
      if (points !== undefined) {
        if (
          !isRecord(points) ||
          (points.yes !== undefined && !isPoints(points.yes)) ||
          (points.no !== undefined && !isPoints(points.no))
        ) {
          problems.push({ code: 'bad_points', severity: 'error', ...at });
        } else if ((points.yes ?? 0) !== 0 || (points.no ?? 0) !== 0) {
          pointsSeen = true;
        }
      }
    } else if (item.points !== undefined) {
      problems.push({ code: 'points_not_allowed', severity: 'error', ...at });
    }

    if (type === 'short_text' || type === 'long_text') {
      const cap =
        type === 'short_text' ? FORM_LIMITS.shortText : FORM_LIMITS.longText;
      const max = item.maxLength;
      if (
        max !== undefined &&
        (typeof max !== 'number' ||
          !Number.isInteger(max) ||
          max < 1 ||
          max > cap)
      ) {
        problems.push({ code: 'bad_max_length', severity: 'error', ...at });
      }
    }
    if (type === 'number') {
      const min = item.min;
      const max = item.max;
      const bad = (n: unknown) =>
        n !== undefined &&
        (typeof n !== 'number' ||
          !Number.isFinite(n) ||
          Math.abs(n) > FORM_LIMITS.numberAbs);
      if (
        bad(min) ||
        bad(max) ||
        (typeof min === 'number' && typeof max === 'number' && min > max) ||
        (item.integer !== undefined && typeof item.integer !== 'boolean')
      ) {
        problems.push({ code: 'bad_number_range', severity: 'error', ...at });
      }
      checkText(
        item.unit,
        FORM_LIMITS.unit,
        null,
        'unit_too_long',
        problems,
        at,
      );
    }
    if (type === 'date' && item.range !== undefined) {
      if (!['past', 'future', 'any'].includes(item.range as string)) {
        problems.push({ code: 'bad_date_range', severity: 'error', ...at });
      }
    }
    if (type === 'consent') {
      checkText(
        item.statement,
        FORM_LIMITS.statement,
        'statement_missing',
        'statement_too_long',
        problems,
        at,
      );
    }

    if (item.showIf !== undefined) {
      const cond = item.showIf;
      if (
        !isRecord(cond) ||
        typeof cond.question !== 'string' ||
        typeof cond.op !== 'string'
      ) {
        problems.push({ code: 'show_if_not_valid', severity: 'error', ...at });
      } else {
        const target = earlier.get(cond.question);
        const later = list.some(
          (other, index) =>
            isRecord(other) &&
            other.id === cond.question &&
            index > list.indexOf(item),
        );
        if (!target) {
          problems.push({
            code: later ? 'show_if_forward' : 'show_if_unknown_question',
            severity: 'error',
            ...at,
          });
        } else if (target === 'info') {
          problems.push({ code: 'show_if_on_info', severity: 'error', ...at });
        } else if (!opsFor(target).includes(cond.op as ShowIfOp)) {
          problems.push({ code: 'show_if_bad_op', severity: 'error', ...at });
        } else if (cond.op !== 'answered') {
          const values =
            cond.op === 'any_of'
              ? Array.isArray(cond.values)
                ? cond.values
                : []
              : [cond.value];
          const allowed =
            target === 'yes_no'
              ? new Set(['yes', 'no'])
              : (optionIds.get(cond.question) ?? new Set<string>());
          if (
            values.length === 0 ||
            !values.every((v) => typeof v === 'string' && allowed.has(v))
          ) {
            problems.push({
              code: 'show_if_bad_value',
              severity: 'error',
              ...at,
            });
          }
        }
      }
    }

    if (item.stop !== undefined) {
      checkStop(item.stop, type, optionIds.get(id), problems, at);
    }

    if (id && !earlier.has(id)) earlier.set(id, type);
  }

  if (answerable === 0) {
    problems.push({ code: 'no_questions', severity: 'error' });
  }
  if (signatures > FORM_LIMITS.signatures) {
    problems.push({ code: 'too_many_signatures', severity: 'error' });
  }
  if (consents > FORM_LIMITS.consents) {
    problems.push({ code: 'too_many_consents', severity: 'error' });
  }

  if (input.scoring !== undefined) {
    const scoring = input.scoring;
    const bands =
      isRecord(scoring) && Array.isArray(scoring.bands) ? scoring.bands : null;
    if (!bands) {
      problems.push({ code: 'scoring_not_valid', severity: 'error' });
    } else {
      if (bands.length > FORM_LIMITS.bands) {
        problems.push({ code: 'too_many_bands', severity: 'error' });
      }
      const keys = new Set<string>();
      let previousMax = -Infinity;
      for (const band of bands) {
        if (!isRecord(band)) {
          problems.push({ code: 'band_not_valid', severity: 'error' });
          continue;
        }
        const key = typeof band.key === 'string' ? band.key : '';
        const atBand = { bandKey: key || undefined };
        if (!BAND_KEY.test(key)) {
          problems.push({ code: 'bad_band_key', severity: 'error', ...atBand });
        } else if (keys.has(key)) {
          problems.push({
            code: 'duplicate_band_key',
            severity: 'error',
            ...atBand,
          });
        }
        keys.add(key);
        checkText(
          band.label,
          FORM_LIMITS.bandLabel,
          'band_label_missing',
          'band_label_missing',
          problems,
          atBand,
        );
        const min = band.min;
        const max = band.max;
        if (
          typeof min !== 'number' ||
          !Number.isInteger(min) ||
          (max !== undefined &&
            (typeof max !== 'number' || !Number.isInteger(max) || max < min)) ||
          (band.flag !== undefined && typeof band.flag !== 'boolean')
        ) {
          problems.push({
            code: 'band_range_not_valid',
            severity: 'error',
            ...atBand,
          });
          continue;
        }
        if (min <= previousMax) {
          problems.push({
            code: 'bands_overlap',
            severity: 'error',
            ...atBand,
          });
        }
        previousMax = typeof max === 'number' ? max : Infinity;
      }
      if (!pointsSeen) {
        problems.push({ code: 'scoring_without_points', severity: 'warning' });
      }
    }
  }

  if (
    input.settings !== undefined &&
    (!isRecord(input.settings) ||
      (input.settings.pharmacistReview !== undefined &&
        typeof input.settings.pharmacistReview !== 'boolean'))
  ) {
    problems.push({ code: 'question_not_valid', severity: 'error' });
  }

  if (!problems.some((p) => p.severity === 'error')) {
    const coverage = frenchCoverage(input as FormSchema);
    if (coverage.done > 0 && coverage.done < coverage.total) {
      problems.push({ code: 'french_incomplete', severity: 'warning' });
    }
  }
  return problems;
}

/** True when nothing stops Publish. */
export function isPublishable(problems: Problem[]): boolean {
  return !problems.some((p) => p.severity === 'error');
}

// ─── Reading a submission back ──────────────────────────────────────────────

/** The few words a submission is printed with, in both languages. */
export const DOCUMENT_WORDS: Record<
  Lang,
  {
    yes: string;
    no: string;
    agreed: string;
    notAnswered: string;
    typedSignature: string;
    drawnSignature: string;
  }
> = {
  en: {
    yes: 'Yes',
    no: 'No',
    agreed: 'Agreed',
    notAnswered: 'Not answered',
    typedSignature: 'Signed by typing',
    drawnSignature: 'Signed',
  },
  fr: {
    yes: 'Oui',
    no: 'Non',
    agreed: 'Accepté',
    notAnswered: 'Sans réponse',
    typedSignature: 'Signé en tapant son nom',
    drawnSignature: 'Signé',
  },
};

export type DocumentRow = {
  questionId: string;
  type: QuestionType;
  /** The question in the submission's language (English when it has none). */
  label: string;
  /** The question in English, when the submission was in French. */
  labelEn?: string;
  /** The answer in words; empty for a drawn signature (shown as an image). */
  answer: string;
  answered: boolean;
  /** For a consent: the statement agreed to, in the submission's language. */
  statement?: string;
  signature?: SignatureAnswer;
};

function answerWords(q: Question, value: Answer, lang: Lang): string {
  const words = DOCUMENT_WORDS[lang];
  switch (q.type) {
    case 'single_choice': {
      const option = q.options.find((o) => o.id === value);
      if (option) return textIn(option.label, lang);
      return typeof value === 'string' ? value : '';
    }
    case 'multi_choice':
      return (Array.isArray(value) ? value : [])
        .map((id) => {
          const option = q.options.find((o) => o.id === id);
          return option ? textIn(option.label, lang) : id;
        })
        .join('; ');
    case 'yes_no':
      return value === 'yes' ? words.yes : words.no;
    case 'number': {
      const unit = q.unit ? ` ${textIn(q.unit, lang)}` : '';
      return typeof value === 'number' ? `${value}${unit}` : '';
    }
    case 'consent':
      return words.agreed;
    case 'signature':
      return isRecord(value) &&
        value.kind === 'typed' &&
        typeof value.name === 'string'
        ? `${words.typedSignature}: ${value.name}`
        : words.drawnSignature;
    case 'short_text':
    case 'long_text':
    case 'date':
      return typeof value === 'string' ? value : '';
    case 'info':
      return '';
  }
}

/**
 * The rows a reader, a print or a PDF shows, in the form's order. `visible` is
 * what the patient saw when they sent it (kept with the submission), so a
 * visible question left blank reads "Not answered"; a hidden one is left out.
 * Never re-evaluates visibility: an old submission reads as it was sent.
 */
export function buildSubmissionDocument(
  schema: FormSchema,
  answers: Answers,
  lang: Lang,
  visible?: readonly string[],
): DocumentRow[] {
  const shown = new Set(visible ?? Object.keys(answers));
  const rows: DocumentRow[] = [];
  for (const q of schema.questions) {
    if (!isAnswerable(q) || !shown.has(q.id)) continue;
    const value = answers[q.id];
    const row: DocumentRow = {
      questionId: q.id,
      type: q.type,
      label: textIn(q.label, lang),
      answer: '',
      answered: value !== undefined,
    };
    if (lang === 'fr' && hasText(q.label, 'fr')) row.labelEn = q.label.en;
    if (value === undefined) {
      row.answer = DOCUMENT_WORDS[lang].notAnswered;
    } else {
      row.answer = answerWords(q, value, lang);
      if (q.type === 'signature' && isRecord(value)) {
        row.signature = value;
        if (value.kind === 'drawn') row.answer = '';
      }
    }
    if (q.type === 'consent') row.statement = textIn(q.statement, lang);
    rows.push(row);
  }
  return rows;
}

// ─── Hashing ────────────────────────────────────────────────────────────────

/** JSON with object keys sorted at every depth, so equal schemas hash equal. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  }
  if (isRecord(value)) {
    const keys = Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort();
    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}
